import { useState } from "react";
import axios from "axios";
import { Settings as SettingsIcon, KeyRound, Eye, EyeOff, RotateCcw, ExternalLink, CheckCircle, XCircle, Loader } from "lucide-react";
import { toast } from "sonner";
import { useSettings, defaultSettings } from "../context/SettingsContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORY_PROVIDERS = [
  {
    id: "groq",
    label: "Groq",
    badge: "GRATIS",
    badgeColor: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    defaultModel: "llama-3.3-70b-versatile",
    keyUrl: "https://console.groq.com/keys",
    hint: "Llama 3.3 70B · Ultrarrápido · Tier gratuito generoso",
  },
  {
    id: "gemini",
    label: "Google Gemini",
    badge: "GRATIS",
    badgeColor: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    defaultModel: "gemini-2.0-flash",
    keyUrl: "https://aistudio.google.com/apikey",
    hint: "Gemini 2.0 Flash · Rápido · Tier gratuito de Google",
  },
  {
    id: "openrouter",
    label: "OpenRouter",
    badge: "GRATIS",
    badgeColor: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    defaultModel: "meta-llama/llama-3.3-70b-instruct:free",
    keyUrl: "https://openrouter.ai/keys",
    hint: "Acceso a modelos :free de múltiples proveedores",
  },
  {
    id: "grok",
    label: "Grok (xAI)",
    badge: "DE PAGO",
    badgeColor: "text-slate-400 bg-slate-400/10 border-slate-400/30",
    defaultModel: "grok-3-mini",
    keyUrl: "https://console.x.ai/",
    hint: "grok-3-mini · Económico · De Elon Musk's xAI",
  },
  {
    id: "mistral",
    label: "Mistral AI",
    badge: "DE PAGO",
    badgeColor: "text-slate-400 bg-slate-400/10 border-slate-400/30",
    defaultModel: "mistral-small-latest",
    keyUrl: "https://console.mistral.ai/api-keys/",
    hint: "mistral-small · Muy eficiente · Empresa europea",
  },
  {
    id: "openai",
    label: "OpenAI",
    badge: "DE PAGO",
    badgeColor: "text-slate-400 bg-slate-400/10 border-slate-400/30",
    defaultModel: "gpt-4o-mini",
    keyUrl: "https://platform.openai.com/api-keys",
    hint: "gpt-4o-mini · Muy capaz · ChatGPT",
  },
  {
    id: "anthropic",
    label: "Anthropic Claude",
    badge: "DE PAGO",
    badgeColor: "text-slate-400 bg-slate-400/10 border-slate-400/30",
    defaultModel: "claude-haiku-4-5",
    keyUrl: "https://console.anthropic.com/settings/keys",
    hint: "claude-haiku · Rápido y preciso · Creadores de Claude",
  },
];

const VOICE_PROVIDERS = [
  { id: "webspeech", label: "Web Speech nativa (gratis)" },
  { id: "elevenlabs", label: "ElevenLabs (premium)" },
  { id: "openai", label: "OpenAI TTS" },
];

const OPENAI_VOICES = [
  { id: "alloy", label: "Alloy" },
  { id: "nova", label: "Nova (cálida)" },
  { id: "shimmer", label: "Shimmer" },
  { id: "echo", label: "Echo" },
  { id: "fable", label: "Fable (narrador)" },
  { id: "onyx", label: "Onyx (grave)" },
];

function SecretInput({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="bg-slate-800/60 border-white/10 text-slate-100 placeholder:text-slate-500 pr-10 rounded-xl focus-visible:ring-amber-400"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-100"
        aria-label={show ? "Ocultar" : "Mostrar"}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function SettingsDialog() {
  const { settings, setSettings, resetSettings } = useSettings();
  const [open, setOpen] = useState(false);
  const [testStatus, setTestStatus] = useState("idle"); // idle | loading | ok | error
  const [testMsg, setTestMsg] = useState("");

  const story = settings.story;
  const voice = settings.voice;

  const updateStory = (patch) => setSettings((prev) => ({ ...prev, story: { ...prev.story, ...patch } }));
  const updateVoice = (patch) => setSettings((prev) => ({ ...prev, voice: { ...prev.voice, ...patch } }));

  const currentProvider = STORY_PROVIDERS.find((p) => p.id === story.provider) ?? STORY_PROVIDERS[0];

  const onProviderChange = (id) => {
    const p = STORY_PROVIDERS.find((x) => x.id === id);
    updateStory({ provider: id, model: p?.defaultModel ?? "" });
    setTestStatus("idle");
    setTestMsg("");
  };

  const onTestKey = async () => {
    if (!story.apiKey) {
      toast.error("Introduce una API key primero");
      return;
    }
    setTestStatus("loading");
    setTestMsg("");
    try {
      const { data } = await axios.post(`${API}/test-key`, {
        provider: story.provider,
        model: story.model || currentProvider?.defaultModel,
        api_key: story.apiKey,
      }, { timeout: 20000 });
      if (data.ok) {
        setTestStatus("ok");
        setTestMsg("Clave válida ✓");
      } else {
        setTestStatus("error");
        setTestMsg(data.error?.slice(0, 120) ?? "Clave inválida");
      }
    } catch {
      setTestStatus("error");
      setTestMsg("No se pudo conectar con el servidor");
    }
  };

  const onReset = () => { resetSettings(); setTestStatus("idle"); toast.success("Ajustes restablecidos"); };
  const onSave = () => { toast.success("Ajustes guardados"); setOpen(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setTestStatus("idle"); setTestMsg(""); } }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="settings-button" aria-label="Ajustes"
          className="text-slate-200 hover:bg-white/10 rounded-full">
          <SettingsIcon className="w-5 h-5" />
        </Button>
      </DialogTrigger>

      <DialogContent data-testid="settings-dialog"
        className="bg-slate-900 border border-white/10 text-slate-100 max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-[Fredoka] text-2xl flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-amber-300" /> Ajustes
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Tus claves se guardan solo en tu navegador y nunca en nuestro servidor.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="story" className="mt-2">
          <TabsList className="bg-slate-800/60 border border-white/10 rounded-full p-1">
            <TabsTrigger value="story" data-testid="settings-tab-story"
              className="rounded-full px-4 data-[state=active]:bg-amber-400 data-[state=active]:text-slate-900">
              Cuento
            </TabsTrigger>
            <TabsTrigger value="voice" data-testid="settings-tab-voice"
              className="rounded-full px-4 data-[state=active]:bg-amber-400 data-[state=active]:text-slate-900">
              Voz
            </TabsTrigger>
          </TabsList>

          {/* ── STORY TAB ── */}
          <TabsContent value="story" className="mt-5 space-y-4">
            {/* Proveedor */}
            <div>
              <Label className="text-slate-300 mb-2 block">Proveedor de IA</Label>
              <Select value={story.provider} onValueChange={onProviderChange}>
                <SelectTrigger data-testid="story-provider-select"
                  className="bg-slate-800/60 border-white/10 text-slate-100 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
                  {["GRATIS", "DE PAGO"].map((group) => (
                    <div key={group}>
                      <div className="px-2 py-1 text-xs text-slate-500 uppercase tracking-wider">{group}</div>
                      {STORY_PROVIDERS.filter((p) => p.badge === group).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Info del proveedor seleccionado */}
            {currentProvider && (
              <div className="rounded-xl bg-slate-800/50 border border-white/10 p-3 text-sm text-slate-300 flex items-start justify-between gap-2">
                <div>
                  <span className={`inline-block text-xs font-bold border rounded-full px-2 py-0.5 mr-2 ${currentProvider.badgeColor}`}>
                    {currentProvider.badge}
                  </span>
                  {currentProvider.hint}
                </div>
                <a href={currentProvider.keyUrl} target="_blank" rel="noopener noreferrer"
                  className="flex-shrink-0 flex items-center gap-1 text-amber-300 hover:text-amber-200 text-xs font-medium whitespace-nowrap">
                  <ExternalLink className="w-3 h-3" /> Obtener key
                </a>
              </div>
            )}

            {/* Modelo */}
            <div>
              <Label className="text-slate-300 mb-2 block">Modelo</Label>
              <Input
                value={story.model || ""}
                onChange={(e) => updateStory({ model: e.target.value })}
                data-testid="story-model-input"
                placeholder={currentProvider?.defaultModel ?? "nombre-del-modelo"}
                className="bg-slate-800/60 border-white/10 text-slate-100 placeholder:text-slate-500 rounded-xl focus-visible:ring-amber-400"
              />
              <p className="text-xs text-slate-500 mt-1">
                Vacío usa el modelo por defecto del proveedor
              </p>
            </div>

            {/* API Key */}
            <div>
              <Label className="text-slate-300 mb-2 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" /> API Key
              </Label>
              <SecretInput
                value={story.apiKey}
                onChange={(e) => { updateStory({ apiKey: e.target.value }); setTestStatus("idle"); setTestMsg(""); }}
                placeholder="Pega aquí tu API key..."
                testId="story-apikey-input"
              />
            </div>

            {/* Test key */}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                onClick={onTestKey}
                disabled={testStatus === "loading" || !story.apiKey}
                data-testid="test-key-button"
                className="bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-full px-5 py-2 border border-white/10 disabled:opacity-40"
              >
                {testStatus === "loading" ? (
                  <><Loader className="w-4 h-4 mr-2 animate-spin" /> Probando...</>
                ) : (
                  "Probar clave"
                )}
              </Button>
              {testStatus === "ok" && (
                <span className="flex items-center gap-1.5 text-emerald-400 text-sm">
                  <CheckCircle className="w-4 h-4" /> {testMsg}
                </span>
              )}
              {testStatus === "error" && (
                <span className="flex items-center gap-1.5 text-rose-400 text-sm">
                  <XCircle className="w-4 h-4" /> {testMsg}
                </span>
              )}
            </div>
          </TabsContent>

          {/* ── VOICE TAB ── */}
          <TabsContent value="voice" className="mt-5 space-y-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Proveedor de voz</Label>
              <Select value={voice.provider} onValueChange={(v) => updateVoice({ provider: v })}>
                <SelectTrigger data-testid="voice-provider-select"
                  className="bg-slate-800/60 border-white/10 text-slate-100 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
                  {VOICE_PROVIDERS.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {voice.provider === "webspeech" && (
              <div className="rounded-xl bg-slate-800/60 border border-white/10 p-4 text-sm text-slate-300">
                Voz del sistema operativo. Gratis, sin clave, calidad variable según dispositivo.
              </div>
            )}

            {voice.provider === "elevenlabs" && (
              <>
                <div>
                  <Label className="text-slate-300 mb-2 flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5" /> API Key de ElevenLabs
                  </Label>
                  <SecretInput
                    value={voice.elevenlabsApiKey}
                    onChange={(e) => updateVoice({ elevenlabsApiKey: e.target.value })}
                    placeholder="sk_..."
                    testId="el-apikey-input"
                  />
                  <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 text-xs mt-1.5">
                    <ExternalLink className="w-3 h-3" /> Obtener key de ElevenLabs
                  </a>
                </div>
                <div>
                  <Label className="text-slate-300 mb-2 block">Voice ID</Label>
                  <Input
                    value={voice.elevenlabsVoiceId || ""}
                    onChange={(e) => updateVoice({ elevenlabsVoiceId: e.target.value })}
                    data-testid="el-voiceid-input"
                    placeholder="21m00Tcm4TlvDq8ikWAM"
                    className="bg-slate-800/60 border-white/10 text-slate-100 placeholder:text-slate-500 rounded-xl focus-visible:ring-amber-400"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">Modelo: eleven_multilingual_v2 (español)</p>
                </div>
              </>
            )}

            {voice.provider === "openai" && (
              <>
                <div>
                  <Label className="text-slate-300 mb-2 flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5" /> API Key de OpenAI
                  </Label>
                  <SecretInput
                    value={voice.openaiApiKey}
                    onChange={(e) => updateVoice({ openaiApiKey: e.target.value })}
                    placeholder="sk-..."
                    testId="openai-tts-apikey-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 mb-2 block">Voz</Label>
                  <Select value={voice.openaiVoice} onValueChange={(v) => updateVoice({ openaiVoice: v })}>
                    <SelectTrigger data-testid="openai-tts-voice-select"
                      className="bg-slate-800/60 border-white/10 text-slate-100 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
                      {OPENAI_VOICES.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 mb-2 block">Modelo TTS</Label>
                  <Select value={voice.openaiModel} onValueChange={(v) => updateVoice({ openaiModel: v })}>
                    <SelectTrigger className="bg-slate-800/60 border-white/10 text-slate-100 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
                      <SelectItem value="tts-1">tts-1 (rápido)</SelectItem>
                      <SelectItem value="tts-1-hd">tts-1-hd (alta calidad)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4 gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onReset} data-testid="settings-reset"
            className="text-slate-300 hover:bg-white/10 rounded-full">
            <RotateCcw className="w-4 h-4 mr-2" /> Restablecer
          </Button>
          <Button onClick={onSave} data-testid="settings-save"
            className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full px-6">
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
